import { Component, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Globals } from 'app/_globals';
import { AuthService } from 'app/_services/auth.service';
import { environment } from 'environments/environment';
import Swal from 'sweetalert2';

@Component({
  selector: 'change-password-modal',
  templateUrl: './change-password-modal.component.html',
  styles: []
})
export class ChangePasswordModalComponent implements OnInit {
  oldPassword = '';
  password1 = '';
  password2 = '';
  passwordErrorText = [];
  errorAlerts: any[] = [];
  isPasswordEmpty = true;
  isPasswordChangeRequired: boolean;

  constructor(
    public globals: Globals,
    public authService: AuthService,
    private activeModal: NgbActiveModal,
  ) {
  }

  ngOnInit() {
    this.isPasswordChangeRequired = JSON.parse(localStorage.getItem(environment.keys.changePasswordRequired));
  }

  public validPasswordInput() {
    if (this.oldPassword && this.password1 && this.password2) {
      this.isPasswordEmpty = false;
    }

    if (!this.oldPassword || !this.password1 || !this.password2) {
      this.isPasswordEmpty = true;
    }
  }

  public validateChangePassword() {
    this.passwordErrorText = [];
    this.errorAlerts = [];

    // lookaheads (non capturing lookahed regex allows these to be concatenated)
    const atLeastOneLowercase = '(?=.*[a-z])';
    const atLeastOneUppercase = '(?=.*[A-Z])';
    const atLeastOneDigit     = '(?=.*[0-9])';
    const specialCharacters   = `[!"#$%&'()*+\\-,:;<=>?@\\[{}\\]|^_\`~£€¥¢×÷./\\\\]`;
    const atLeastOneSpecial   = `(?=.*${specialCharacters})`;
    const minimumLength       = '(?=.{8,})';

    // Final regex
    const strongRegex = new RegExp(
      `^${atLeastOneLowercase}${atLeastOneUppercase}${atLeastOneDigit}${atLeastOneSpecial}${minimumLength}.*$`,
      'u'
    );

    const strongText = [
      '8 characters',
      '1 uppercase character',
      '1 lowercase character',
      '1 numeric character',
      '1 special character (e.g. $ £ ^ % * & ! | @)'
    ];

    if (!this.oldPassword || !this.password1 || !this.password2) {
      this.passwordErrorText = ['Please complete password fields'];
    } else if (this.password1 !== this.password2) {
      this.passwordErrorText = ['Passwords Do Not Match'];
    } else if (!strongRegex.test(this.password1)) {
      const err = ['Password must have at least:'];

      if (this.containsThaiScript(this.password1)) {
        const noSpaces8Chars = /^(?=\S{8,}$)[\p{Script=Latin}\p{Script=Thai}!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]*$/u;
        const strongThaiText = [
          'at least 8 characters',
          'no spaces',
        ];
        if (!noSpaces8Chars.test(this.password1)) {
          this.passwordErrorText = err.concat(strongThaiText);
        } else {
          this.passwordErrorText = [];
          this.sendChangePassword();
        }
      } else {
        this.passwordErrorText = err.concat(strongText);
      }

    } else {
      this.passwordErrorText = [];
      this.sendChangePassword();
    }
  }

  private containsThaiScript(password: string): boolean {
    const thaiScriptPattern = new RegExp('[\u0E00-\u0E7F\u2000-\u206F]');
    return thaiScriptPattern.test(password);
  }

  public submit() {
    this.validateChangePassword();
  }

  public sendChangePassword() {
    const body = {
      oldPassword: this.oldPassword,
      newPassword: this.password1
    }

    this.authService.changePassword(body).subscribe(
      (user: any) => {
        Swal.fire('Success', 'Password has been updated!', 'success');
        this.passwordErrorText = [];
        this.isPasswordChangeRequired = false;
        this.authService.logout();
        this.close();
      }, (error) => {
        this.errorAlerts = [];
        if (error.error && error.error.title) {
          if (error.error.fieldErrors && error.error.fieldErrors.length > 0 && error.error.title === 'Method argument not valid') {
            this.errorAlerts.push({ severity: 'warn', summary: 'Invalid field entry: ' + error.error.fieldErrors[0].field });
          } else {
            this.errorAlerts.push({ severity: 'warn', summary: error.error.title });
          }
        } else {
          throw error;
        }
      }
    );
  }

  public passwordChangeRequired() {
    if (this.isPasswordChangeRequired) {
      this.activeModal.close('refresh');
    } else {
      this.close();
    }
  }

  public close() {
    if (this.isPasswordChangeRequired) {
      Swal.fire({
        title: 'Are you sure?',
        text: 'You will be signed out until you change your password.',
        type: 'warning',
        showCancelButton: true,
        customClass: {
          confirmButton: 'confirm-danger',
        },
        confirmButtonText: 'Yes, sign out',
      }).then((result) => {
        if (result.value) {
          this.activeModal.dismiss();
          this.authService.logout();
        }
      })
    } else {
      this.activeModal.dismiss();
    }
  }

}
